import { motion } from "motion/react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import { AnimatedDivider } from "../components/AnimatedDivider";
import { Helmet } from "react-helmet-async";

import { ScrollShadow } from "../components/ScrollShadow";

function WhiteBar() {
  return (
    <motion.div 
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
      className="h-[3px] w-full bg-white my-16 origin-left" 
    />
  );
}

export function About() {
  const location = useLocation();
  const { hasLoaded, handleNavClick, isScrolledPop } = useOutletContext<{ hasLoaded: boolean, handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void, isScrolledPop?: boolean }>();
  const [isDocking, setIsDocking] = useState(() => Boolean(hasLoaded && location.state?.fromNav));

  useEffect(() => {
    if (isDocking) {
      const timer = setTimeout(() => setIsDocking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDocking]);

  return (
    <>
      <Helmet>
        <title>About • Atharva Dhekne | tacitonic</title>
        <meta name="description" content="Learn more about Atharva Dhekne (tacitonic), Mastering and Immersive Audio Engineer based in NYC at Sterling Sound and Eventide Audio." />
        <meta name="robots" content="index, follow, max-image-preview:none" />
        <meta name="googlebot" content="index, follow, max-image-preview:none" />
        <link rel="canonical" href="https://tacitonic.com/about" />
        <meta property="og:title" content="About • Atharva Dhekne | tacitonic" />
        <meta property="og:description" content="Learn more about Atharva Dhekne (tacitonic), Mastering and Immersive Audio Engineer based in NYC at Sterling Sound and Eventide Audio." />
        <meta property="og:url" content="https://tacitonic.com/about" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@id": "https://tacitonic.com/#person"
            },
            "url": "https://tacitonic.com/about",
            "name": "About Atharva Dhekne (tacitonic)"
          })}
        </script>
      </Helmet>
      <main className="pt-40 pb-32 px-6 max-w-7xl mx-auto min-h-screen">
      <div className="mb-16 pb-8 relative">
        <motion.h1 
          layoutId={isScrolledPop ? undefined : `page-title-About`}
          initial={(!hasLoaded || isScrolledPop) ? { opacity: 0, scale: 0.95, y: 10 } : undefined}
          animate={{ opacity: 1, scale: 1, y: 0, color: "#FEFDF9" }}
          className="text-[31px] md:text-[52px] lg:text-[64px] font-bold tracking-tighter uppercase leading-none font-display w-fit origin-left text-white"
          transition={{ 
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
          }}
        >
          About
        </motion.h1>
        {!isDocking && (
          <div className="absolute bottom-0 left-0 w-full">
            <AnimatedDivider className="h-[3px] bg-white" />
          </div>
        )}
      </div>

      <div className={`transition-opacity duration-700 delay-300 ${isDocking ? 'opacity-0' : 'opacity-100'}`}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <ScrollShadow className="space-y-12 text-xl md:text-3xl font-responsive-bold tracking-tight leading-relaxed opacity-90">
            <p>
              Atharva Dhekne “tacitonic” is currently a Mastering and Immersive Engineer at Sterling Sound based out of the New York City area; also working at Eventide Audio and freelancing with Kevin Killen, David Byrne among others. Atharva is a graduate in Music Technology from the Steinhardt School of Culture, Education, and Human Development at New York University (NYU). He holds a Bachelor of Engineering degree in Computer Engineering from the University of Pune. Atharva has been playing and learning classical piano since the age of 5. His research interests include mastering, spatial audio, interactive audio, game audio, production, audio programming, and product design.
            </p>
            
            <p>
              At Sterling Sound, Atharva works with highly demonstrated engineers such as Chris Gehringer, Greg Calbi, Steve Fallone, Joe LaPorta, and Will Quinnell on a wide variety of chart-topping music. View his updated <a href="/discography" onClick={(e) => handleNavClick(e, '/discography')} className="text-white hover:opacity-100 underline underline-offset-4 transition-colors">engineering discography</a> at Sterling Sound. Previously he has also worked on sessions with influential artists including Ariana Grande, Dua Lipa, Alicia Keys, Justin Bieber, J Balvin, Future, Travis Scott, Lil Nas X, Cardi B, Lil Uzi Vert, Charli XCX, Swizz Beatz, Offset, JT, Lil Wayne, Morelli, Megan Thee Stallion, Tyla, Lil Baby, A Boogie Wit Da Hoodie, G-Eazy, Destroy Lonely, Polo G, Jean Nelson, Simone Torres, Addison Rae, and more.
            </p>
  
            <p>
              A research and development professional with cross-domain experience in audio, music, gaming, technology, production, project management, open source, design, and UI/UX development, Atharva has worked on projects spanning the globe and impacting millions. Notably, he has done research and worked with Rockstar Games, Jungle City Studios, NYU, Clive Davis Institute of Recorded Music, WordPress, Google, the Apache Software Foundation, Muellners Foundation, and the Automotive Research Association of India (ARAI; Ministry of Industries, Government of India). Atharva has also been an active member in organizing multiple TEDx events, Google Developer Student Clubs, AES Conventions and IRCAM Forums.
            </p>

            <p>
              Atharva has collaborated with a distinctive ensemble of distinguished artists around the world. He appreciates a diverse scope of musical genres and is influenced by an extensive gamut of creative and musical artists. See his <a href="https://open.spotify.com/playlist/6Z8q2nyJGWbUp2ON4KnHX8?si=e24d77083c05487c" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-100 underline underline-offset-4 transition-colors">Favorites</a> playlists for particulars. Atharva extends from this extensive sonic vocabulary to realize a unique multi-modal artistic identity.
            </p>

            <p>
              Chat with Atharva about gaming, typography, geo-politics, languages, or the Oxford comma, and you’ll get his absolute attention. Atharva wishes to continue pursuing his passion for music as his profession. One more thing he wishes, is to not write this bio in third person.
              <br />
              You can view his <a href="https://docs.google.com/document/d/14wsXxT4mBwGRCsuh87n4gUnSXkHTb4y4CqyFWz0gxos/edit?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-100 underline underline-offset-4 transition-colors">Resume</a> and <a href="https://docs.google.com/document/d/1hKiQt98BPi-NPSPzLtxJTYC8WREZpzg3nqoApxXRObs/edit?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-100 underline underline-offset-4 transition-colors">Curriculum Vitae (CV)</a>.
            </p>
          </ScrollShadow>
        </motion.div>
      </div>
    </main>
    </>
  );
}
